import React, {useState} from 'react';
import {View, Text, StyleSheet} from 'react-native';
import {useSelector, useDispatch} from 'react-redux';
import {LinearGradient} from 'expo-linear-gradient';

import OrderNumberHeader from './OrderNumberHeader';

import styles from '../../style/scanpack';
import globalStyles from '../../style/global';
import commonStyles from '../../style/commonStyles';

import StockComponents from './StockComponents';
import LocationHeader from './LocationHeader';
import ToteStatusGrid from './ToteGrid';
import OrderItemSuggested from '../../commons/orderItemSuggested';
import placeholder from '../../../assets/placeholder.jpg';
import DisplaySuggestedImageDesktop from '../scanpack/displaySuggestedImageDesktop';
import ScanBarcodeSection from './ScanBarcodeSection';
import FocusBar from './FocusBar';
import CartReadyBanner from '../scanToCart/CartReadyBanner';
import {redirectToProductDetail, redirectToOrderDetail, setMainInputFocus, setPrompt, setFocusedOrder} from '../../actions/ScanToCartScanningActions';
import AddBarcode from '../scanpack/addBarcode';
import ScanProgress from './ScanProgress';
import CartIdLabel from './CartIdLabel';
import useWidth from './useWidth';
import DisplayImage from '../scanpack/displaySuggestedImage';
import { PickCompleteComponent } from './PickComplete';
import { setActiveTote, addBarcodeToCartItem } from '../../actions/scanToCartActions';
import useSpeakOnItemChange from './hooks/useSpeakOnItemChange';
import { getFontSize, getWidth, getMargin, FOCUS_DESKTOP_MIN_WIDTH } from './utils/responsiveUtils';

const SuggestedItemSection = () => {
  const dispatch = useDispatch();
  const windowWidth = useWidth();
  const [addBarcodeItem, setAddBarcodeItem] = useState(null);
  const [addBarcodeText, setAddBarcodeText] = useState('');
  const [isSavingBarcode, setIsSavingBarcode] = useState(false);

  const {currentOrder, barcodeValue, activeCart, nextPickItem, isCartView,cartScanComplete} =
    useSelector(state => ({
      currentOrder: state.scanToCartScanning.currentOrder,
      barcodeValue: state.scanToCartScanning.barcodeValue,
      isCartView: state.scanToCart.isCartView,
      nextPickItem: state.scanToCart.nextPickItem,
      activeCart: state.scanToCart.activeCart,
      cartScanComplete: state.scanToCartScanning.cartScanComplete,
    }));

  // Extract current item and tote from nextPickItem
  const currentItem = nextPickItem?.item || null;
  const currentTote = nextPickItem?.tote || null;

  // Voice picking - speaks item location on mount and when item changes
  useSpeakOnItemChange(currentItem);

  // Functions that match the NextItems component implementation
  const handleRedirectToItemDetail = item => {
    dispatch(redirectToProductDetail(item.product_id));
  };

  const handleAddBarcode = item => {
    dispatch(setMainInputFocus(false));
    setAddBarcodeText('');
    setAddBarcodeItem(item);
  };

  const closeAddBarcode = () => {
    setAddBarcodeItem(null);
    setAddBarcodeText('');
    dispatch(setMainInputFocus(true));
  };

  const submitAddBarcode = async () => {
    if (isSavingBarcode || !addBarcodeText.trim()) return;
    setIsSavingBarcode(true);
    const result = await dispatch(addBarcodeToCartItem(addBarcodeItem, addBarcodeText));
    setIsSavingBarcode(false);
    closeAddBarcode();
    if (!result?.ok) {
      dispatch(setPrompt(true, result?.error, 'alert'));
    }
  };

  const handleToteOnPress = (tote) => {
    if(cartScanComplete && !activeCart.isPicked){
      dispatch(setActiveTote(tote?.id))
    }
  }

  const handleToteFocus = (orderId) => {
    dispatch(setFocusedOrder(orderId));
  };

  const handleOrderPress = () => {
    dispatch(redirectToOrderDetail());
  };

  const handleGetOrderData = () => {
    console.log('Get order data - refresh functionality');
    // Dispatch action to refresh order data
  };

  // Helper function to create location row
  const createLocationRow = (key, label, value, backgroundColor) => (
    <View key={key} style={[componentStyles.locationRow, { backgroundColor }]}>
      <Text style={componentStyles.locationLabel}>
        {label} :
      </Text>
      <Text style={componentStyles.locationValue}>
        {value}
      </Text>
    </View>
  );

  // Generate attribute rows from current item
  const getAttributeRows = () => {
    if (!currentItem) return [];

    const rows = [];
    const locations = [
      { key: 'L1', label: 'L1', value: currentItem.location, color: '#b6cadd' },
      { key: 'L2', label: 'L2', value: currentItem.location2, color: '#768da5' },
      { key: 'L3', label: 'L3', value: currentItem.location3, color: '#5a7792' }
    ];

    locations.forEach(({ key, label, value, color }) => {
      if (value) {
        rows.push(createLocationRow(key, label, value, color));
      }
    });

    return rows;
  };

  // Common props for both display components
  const getDisplayImageProps = () => ({
    source: placeholder,
    defaultSource: placeholder,
    scannedQty: currentItem.scanned_qty || 0,
    totalQty: (currentItem.qty_remaining || 0) + (currentItem.scanned_qty || 0),
    remainingQty: currentItem.qty_remaining || 0,
    images: currentItem.images || [],
    id: currentOrder?.id,
    order: currentOrder,
    onPressGetOrderData: handleGetOrderData,
    getWidth: () => getWidth(windowWidth),
    getMargin: () => getMargin(windowWidth)
  });

  // Render display image component based on screen size
  const renderDisplayImage = () => {
    const props = getDisplayImageProps();
    
    if (windowWidth > 900) {
      return <DisplaySuggestedImageDesktop {...props} hideMenu={true} />;
    }
    
    return (
      <DisplayImage
        {...props}
        scanned_qty={props.scannedQty}
        total_qty={props.totalQty}
        hideMenu={true}
      />
    );
  };

  if(cartScanComplete && !currentItem){
    return (
      <View style={componentStyles.mainContainer}>
        <View style={styles.RFPScreen}>
          <View style={globalStyles.nextItemFlex}>
            <PickCompleteComponent />
            <ScanBarcodeSection windowWidth={windowWidth} />
            <CartReadyBanner />
            <ToteStatusGrid onTotePress={handleToteOnPress} onToteFocus={handleToteFocus}/>
            <View style={componentStyles.cartIdRow}>
              <CartIdLabel windowWidth={windowWidth} />
            </View>
          </View>
        </View>
      </View>
    )
  }

  // Show loading or empty state if no active cart or current item
  if (!activeCart || !currentItem) {
    return (
      <View style={componentStyles.emptyStateContainer}>
        <Text style={componentStyles.emptyStateText}>
          {!activeCart ? 'No active cart' : 'No items to pick'}
        </Text>
      </View>
    );
  }

  return (
    <View style={componentStyles.mainContainer}>
      <View style={styles.RFPScreen}>
        <View style={globalStyles.nextItemFlex}>
          {windowWidth > 900 && (
            <LinearGradient
              start={{x: 0, y: 1}}
              end={{x: 0, y: 0}}
              colors={['#000000', '#00000006', '#00000006', '#000000']}
              style={[styles.actionButtonsView]}>
              <OrderNumberHeader
                orderIncrementId={currentOrder?.increment_id}
                onOrderPress={handleOrderPress}
              />
            </LinearGradient>
          )}

          {cartScanComplete && <PickCompleteComponent />}
          <ScanBarcodeSection windowWidth={windowWidth} />
          {windowWidth < FOCUS_DESKTOP_MIN_WIDTH && <FocusBar />}

          <StockComponents />
          {!(cartScanComplete || activeCart.isPicked) && (
            <LocationHeader windowWidth={windowWidth} />
          )}

          {!isCartView && (
            <View style={commonStyles.nextItemRowFlex}>
              <OrderItemSuggested
                redirectToItemDetail={handleRedirectToItemDetail}
                item={currentItem}
                name={'suggestedItemSingle'}
                testID={'redirectToItem'}
                displayRow={getAttributeRows()}
                addBarcodeFun={handleAddBarcode}
                getFontSize={() => getFontSize(windowWidth)}
                width={windowWidth}
              />

              {renderDisplayImage()}
            </View>
          )}

          {isCartView && <ToteStatusGrid onTotePress={handleToteOnPress} onToteFocus={handleToteFocus}/>}
          {!(cartScanComplete || activeCart.isPicked) && (
            <ScanProgress windowWidth={windowWidth} />
          )}
          {(cartScanComplete || activeCart.isPicked) && (
            <View style={componentStyles.cartIdRow}>
              <CartIdLabel windowWidth={windowWidth} />
            </View>
          )}
        </View>
      </View>
      {addBarcodeItem && (
        <AddBarcode
          closeAlert={closeAddBarcode}
          addBarcodeSubmit={submitAddBarcode}
          addBarcodeText={addBarcodeText}
          addBarcodeChange={setAddBarcodeText}
          addBarcodeWindow={!!addBarcodeItem}
        />
      )}
    </View>
  );
};

const componentStyles = StyleSheet.create({
  mainContainer: {
    flex: 4
  },
  cartIdRow: {
    alignItems: 'center',
    paddingVertical: 15
  },
  emptyStateContainer: {
    flex: 4,
    justifyContent: 'center',
    alignItems: 'center'
  },
  emptyStateText: {
    color: '#fff',
    fontSize: 18
  },
  locationRow: {
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 8,
    alignItems: 'center'
  },
  locationLabel: {
    fontSize: 18,
    paddingTop: 2,
    ...globalStyles.nextItemText
  },
  locationValue: {
    fontSize: 28,
    textAlign: 'left',
    ...globalStyles.nextItemText
  }
});

export default SuggestedItemSection;
